import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { createTask, updateTask, getTask, getUsers, getUser } from '../service/weexpertService';
import { useTranslation } from "react-i18next";
import { Button } from '@/components/ui/button';
import { InputWithAlert } from '@/components/ui/input-with-alert';
import { SelectWithAlert } from '@/components/ui/select-with-alert';
import { TaskDateTimePicker } from '@/components/ui/task-datetime-picker';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import { Loader } from '@/components/ProtectedRoute';

const TaskCreatePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('tasks');
  const queryClient = useQueryClient();

  const isEditing = !!id;
  const [isLoading, setIsLoading] = useState(false);
  const [inputChecked, setInputChecked] = useState(false);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [taskType, setTaskType] = useState('TEXT');
  const [deadline, setDeadline] = useState(null); // Don't set default, let component handle it
  const [assignedToId, setAssignedToId] = useState('');

  const { data: user, isLoading: isUserLoading, isFetched: userIsFetched } = useQuery({ queryKey: 'userMe', queryFn: getUser, retry: false });
  const { data: users} = useQuery('users', () => getUsers(100, 0));
  const { data: taskData } = useQuery(['task', id], () => getTask(id), {
    enabled: !!id
  });

  const nonClientUsers = users?.data?.users?.filter(user =>
    user.roles !== 'CLIENT'
  ) || [];

  useEffect(() => {
    if (isEditing && taskData?.data) {
      const task = taskData.data;
      setTitle(task.title || '');
      setContent(typeof task.content === 'string' ? task.content : JSON.stringify(task.content, null, 2));
      setTaskType(task.taskType || 'TEXT');
      setDeadline(task.deadline ? new Date(task.deadline) : new Date());
      setAssignedToId(task.assignedTo?.id || '');
    }
  }, [isEditing, taskData, id]);


  const handleSubmit = async () => {
    setInputChecked(true);

    if (!title || !content || !deadline || !assignedToId) {
      toast({
        title: t("error"),
        description: t("fill_required_fields"),
        variant: "destructive"
      });
      return;
    }
    
    // Validate that deadline is a valid Date object
    if (!(deadline instanceof Date) || isNaN(deadline.getTime())) {
      toast({
        title: t("error"),
        description: "Invalid deadline date. Please select a valid date and time.",
        variant: "destructive"
      });
      return;
    }

    // Check if deadline is in the future (with 1 minute tolerance for timezone conversion)
    const now = new Date();
    const oneMinuteAgo = new Date(now.getTime() - 60000);

    if (deadline < oneMinuteAgo) {
      toast({
        title: t("error"),
        description: t("deadline_future_error"),
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Additional safety check before calling toISOString()
      let deadlineISO;
      try {
        deadlineISO = deadline.toISOString();
      } catch (isoError) {
        toast({
          title: t("error"),
          description: "Failed to process deadline. Please try selecting the date and time again.",
          variant: "destructive"
        });
        return;
      }

      const taskData = {
        title: title,
        content: taskType === 'TEXT' ? content : JSON.parse(content),
        taskType: taskType,
        deadline: deadlineISO,
        assignedToId: parseInt(assignedToId)
      };

      if (isEditing) {
        await updateTask(id, taskData);
        toast({
          title: t("success"),
          description: t("task_updated_success"),
          variant: "default"
        });
      } else {
        await createTask(taskData);
        toast({
          title: t("success"),
          description: t("task_created_success"),
          variant: "default"
        });
      }

      queryClient.invalidateQueries('tasks');
      queryClient.invalidateQueries(['task', id]);
      navigate('/admin/tasks');
    } catch (error) {
      console.error('Error saving task:', error);
      toast({
        title: t("error"),
        description: `Failed to ${isEditing ? 'update' : 'create'} task: ${error.response?.data?.message || error.message}`,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };


    if (isUserLoading) {
      return <Loader />
    }
    if (user.data.roles === 'CLIENT') {
      navigate('/404')
    }

  return (
    <div className="w-full px-8 py-10 bg-grayLightMainBg">
      <div className="text-4xl">
        {isEditing ? t('edit_task') : t('create_new_task')}
      </div>

      <div className="w-9/12 pt-4">
        <div className="w-full pt-6">
          <InputWithAlert
            label={t('task_title') + ' *'}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={inputChecked && !title}
            errorMessage={t('empty_data_invalid')}
            inputClassName="h-12 mt-2 mb-4 bg-white"
            placeholder={t('enter_task_title')}
          />

          <SelectWithAlert
            label={t('task_type') + ' *'}
            options={[
              { value: 'TEXT', label: t('text') },
              { value: 'FORM', label: t('form') }
            ]}
            value={taskType}
            onChangeValue={setTaskType}
            inputClassName="h-12 mt-2 mb-4 bg-white"
          />

          <SelectWithAlert
            label={t('assign_to') + ' *'}
            placeholder={t('select_user_placeholder')}
            options={nonClientUsers.map(user => ({
              value: user.id.toString(),
              label: `${user.firstName} ${user.lastName} (${user.roles})`
            }))}
            value={assignedToId ? assignedToId.toString() : undefined}
            onChangeValue={setAssignedToId}
            error={inputChecked && !assignedToId}
            errorMessage={t('empty_data_invalid')}
            inputClassName="h-12 mt-2 mb-4 bg-white"
          />

          <TaskDateTimePicker
            key={`task-datetime-${id || 'new'}`}
            label={t('deadline') + ' *'}
            datetime={deadline}
            setDatetime={setDeadline}
            error={inputChecked && !deadline}
            errorMessage={t('empty_data_invalid')}
            inputClassName="h-12 mt-2 mb-4 bg-white"
          />

          <div className="mb-4">
            <div className="font-medium mb-2">{t('content')} *</div>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={
                taskType === 'TEXT'
                  ? t('enter_task_description')
                  : t('enter_form_json')
              }
              rows={8}
              className="bg-white"
            />
            {taskType === 'FORM' && (
              <p className="text-sm text-gray-500 mt-1">
                {t('form_json_help')}
              </p>
            )}
          </div>
        </div>

        <div className="flex space-x-4 pt-4">
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="bg-blueLightAccent text-blueText hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none"
          >
            {isLoading ? t('saving') : (isEditing ? t('update_task') : t('create_task'))}
          </Button>

          <Button
            variant="outline"
            onClick={() => navigate('/admin/tasks')}
            disabled={isLoading}
          >
            {t('cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TaskCreatePage;
